import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/common';
import { Badge } from '@/components/data-display/Badge';
import { usePermission } from '@/hooks/usePermission';
import { useAuthStore } from '@/store/authStore';
import { errorMessage, errorStatus, useScreening, useScreeningContext } from './api';
import { label, type ScreeningContext, type ScreeningDetail } from './types';
import { ScreeningBadge, Tabs, formatDecimal, time } from './shared';
import { ScreeningActions } from './ScreeningActions';
import {
  CounterpartyAml,
  Overview,
  ReviewHistory,
  Rules,
  StandaloneAml,
  TechnicalDetails,
  amlSummary,
} from './ScreeningEvidence';

const SOURCE_LABELS: Record<string, string> = {
  didit: 'Didit (automatic)',
  didit_override: 'Didit console reviewer',
  manual: 'CBS manual decision',
  mock: 'Mock provider',
};

function partyName(name: string | null, fallback: string) {
  return name ?? <span className="text-[#999]">{fallback}</span>;
}

/** Readable transaction summary. Only values the CBS recorded are shown; none are derived. */
function TransactionHeader({
  row,
  context,
}: {
  row: ScreeningDetail;
  context: ReturnType<typeof useScreeningContext>;
}) {
  const outgoing = row.transaction_type === 'outbound';
  const data: ScreeningContext | undefined = context.data;
  const record = data?.record ?? null;
  const client = partyName(data?.client.name ?? null, `Client ${row.client_id} (name unavailable)`);
  const counterparty = partyName(
    data?.counterparty.name ?? null,
    outgoing ? 'Beneficiary name not recorded' : 'Sender name not recorded',
  );
  const [from, to] = outgoing ? [client, counterparty] : [counterparty, client];
  return (
    <section
      aria-labelledby="transaction-heading"
      className="rounded-lg border border-[#e8e8e8] bg-white p-4 sm:p-6"
    >
      <p className="text-xs font-semibold uppercase tracking-[1.5px] text-[#6B0F1A]">
        <span aria-hidden="true">{outgoing ? '↗ ' : '↙ '}</span>
        {outgoing ? 'Outgoing payment' : 'Incoming payment'}
      </p>
      {context.isPending ? (
        <p role="status" className="mt-3 text-sm text-[#777]">
          Loading transaction details…
        </p>
      ) : context.isError ? (
        <div role="alert" className="mt-3 rounded-md bg-[#ffebee] p-3 text-sm text-[#c62828]">
          Transaction details are unavailable ({errorMessage(context.error)}). The screening below
          is unaffected.{' '}
          <Button variant="outline" size="sm" onClick={() => void context.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
            <h2 id="transaction-heading" className="break-words text-xl font-bold text-[#1a1a1a]">
              {record?.reference ?? 'No payment reference recorded'}
            </h2>
            <p className="text-2xl font-extrabold text-[#1a1a1a]">
              {record?.amount && record.currency ? (
                `${formatDecimal(record.amount)} ${record.currency}`
              ) : (
                <span className="text-base font-medium text-[#999]">Amount not recorded</span>
              )}
            </p>
          </div>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-[1fr_auto_1fr] sm:items-center">
            <div className="min-w-0">
              <dt className="text-xs text-[#777]">From</dt>
              <dd className="break-words font-medium text-[#333]">{from}</dd>
            </div>
            <span aria-hidden="true" className="hidden text-[#999] sm:block">
              →
            </span>
            <div className="min-w-0">
              <dt className="text-xs text-[#777]">To</dt>
              <dd className="break-words font-medium text-[#333]">{to}</dd>
            </div>
          </dl>
          {!record && (
            <p className="mt-3 text-sm text-[#777]">
              The CBS has no record of this transaction to show its amount and parties.
            </p>
          )}
        </>
      )}
      <p className="mt-3 text-xs text-[#777]">Screening requested {time(row.requested_at)}</p>
    </section>
  );
}

function Stat({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="min-w-0 rounded-md border border-[#eee] bg-white p-3">
      <dt className="text-xs text-[#777]">{term}</dt>
      <dd className="mt-1 break-words text-sm font-medium text-[#333]">{children}</dd>
    </div>
  );
}

/** Screening, provider and financial states are deliberately separate facts. */
function DecisionStrip({
  row,
  context,
}: {
  row: ScreeningDetail;
  context: ReturnType<typeof useScreeningContext>;
}) {
  const financial = context.data?.record?.status;
  return (
    <section aria-label="Screening status">
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Stat term="Screening decision">
          <span className="flex flex-wrap items-center gap-2">
            <ScreeningBadge status={row.status} />
            <span className="text-xs font-normal text-[#777]">
              {row.applied_at ? `Applied ${time(row.applied_at)}` : 'Not applied'}
            </span>
          </span>
        </Stat>
        <Stat term="Provider status">{row.result.raw_status}</Stat>
        <Stat term="Decision source">
          {row.decision_source
            ? (SOURCE_LABELS[row.decision_source] ?? label(row.decision_source))
            : 'No decision yet'}
        </Stat>
        <Stat term="Hold reason">
          {row.hold_reason ? <Badge variant="warning">{label(row.hold_reason)}</Badge> : 'None'}
        </Stat>
        <Stat term="Last successful sync">
          {row.last_synced_at ? time(row.last_synced_at) : 'No successful sync recorded'}
        </Stat>
        <Stat
          term={row.transaction_type === 'outbound' ? 'CBS payment status' : 'CBS incoming status'}
        >
          {context.isPending
            ? 'Loading…'
            : context.isError
              ? 'Unavailable'
              : financial
                ? label(financial)
                : 'Not recorded'}
        </Stat>
      </dl>
      {row.enforcement_mode === 'shadow' ? (
        <p className="mt-3 rounded-lg border border-[#ffe0b2] bg-[#fff8ee] p-3 text-sm text-[#784600]">
          <strong>Shadow mode.</strong> This screening decision is recorded for comparison only. It
          does not execute, release, credit or reject the payment; operators still decide the
          payment in the CBS.
        </p>
      ) : (
        <p className="mt-3 rounded-lg border border-[#eee] bg-[#fafafa] p-3 text-sm text-[#666]">
          Enforcement mode: {label(row.enforcement_mode)}. The screening decision and its
          application are separate from settlement; check the CBS payment status for the financial
          outcome.
        </p>
      )}
    </section>
  );
}

export function ScreeningDetailContent({ row }: { row: ScreeningDetail }) {
  const context = useScreeningContext(row.screening_id);
  const result = row.result;
  const matched = result.rule_runs.filter((rule) => rule.matched).length;
  const history = row.events.length + (row.override.status === 'none' ? 0 : 1);
  return (
    <div className="space-y-5">
      <TransactionHeader row={row} context={context} />
      <DecisionStrip row={row} context={context} />
      <ScreeningActions key={row.screening_id} row={row} />
      <Tabs
        label="Screening evidence"
        items={[
          { id: 'overview', label: 'Overview', content: <Overview row={row} /> },
          {
            id: 'rules',
            label: `Rules (${matched} matched)`,
            content: <Rules rules={result.rule_runs} />,
          },
          {
            id: 'counterparty',
            label: 'Counterparty AML',
            content: (
              <>
                <h3 className="sr-only">Counterparty AML findings</h3>
                <p className="text-sm text-[#666]">
                  {amlSummary(
                    result.counterparty_aml,
                    result.transaction_evidence?.counterparty_aml.availability === 'unavailable',
                  )}
                </p>
                <CounterpartyAml result={result} />
              </>
            ),
          },
          {
            id: 'standalone',
            label: 'Standalone AML',
            content: <StandaloneAml result={result} />,
          },
          {
            id: 'history',
            label: `Review history (${history})`,
            content: <ReviewHistory row={row} />,
          },
          { id: 'technical', label: 'Technical details', content: <TechnicalDetails row={row} /> },
        ]}
      />
    </div>
  );
}

export function ScreeningDetailPage() {
  const { screeningId = '' } = useParams();
  const tenant = useAuthStore((s) => s.user?.tenant_cod);
  const { has, hasRole } = usePermission();
  const query = useScreening(screeningId);
  const valid = /^[1-9]\d{0,18}$/.test(screeningId);
  const status = query.isError ? errorStatus(query.error) : null;
  return (
    <div className="space-y-5">
      <Link to="/transaction-monitoring" className="text-sm font-semibold text-[#6B0F1A] underline">
        ← Compliance Panel
      </Link>
      <h1 className="text-[26px] font-extrabold text-[#1a1a1a]">Compliance screening</h1>
      {!hasRole('cbs_admin') || !has('COMPLIANCE_VIEW') ? (
        <p role="alert" className="rounded-lg bg-[#fafafa] p-4 text-sm text-[#666]">
          You do not have permission to view compliance screenings (COMPLIANCE_VIEW).
        </p>
      ) : !valid ? (
        <p role="alert">Invalid screening ID.</p>
      ) : query.isPending ? (
        <p role="status">Loading screening…</p>
      ) : query.isError ? (
        <div role="alert" className="rounded-lg bg-[#ffebee] p-4 text-sm text-[#c62828]">
          {status === 404
            ? 'Screening not found for the active tenant. '
            : status === 403
              ? 'You do not have permission to view this screening. '
              : status === 503
                ? 'Compliance screening is unavailable. '
                : ''}
          {errorMessage(query.error)}{' '}
          {status !== 404 && status !== 403 && (
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              Retry
            </Button>
          )}
        </div>
      ) : (
        <ScreeningDetailContent key={`${tenant}-${screeningId}`} row={query.data} />
      )}
    </div>
  );
}
